import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMentions } from "../../hooks/useMentions";
import { getDefaultUserAvatar } from "winslow-core";
import { ValidatedImage } from "./ValidatedImage";
import {
  openEmployeeProfileSidebar,
  openDocumentPreviewModal,
  selectCompanyDocuments,
} from "../../redux/features/agent/agentSlice";
import "./MentionText.css";

export type MentionTextProps = {
  text: string;
  className?: string;
  onClick?: (mentionId: string, mentionType: string) => void;
};

export const MentionText: React.FC<MentionTextProps> = ({
  text,
  className = "",
  onClick,
}) => {
  const { parseMentions } = useMentions();
  const dispatch = useDispatch();
  const companyDocuments = useSelector(selectCompanyDocuments);

  const handleMentionClick = (
    e: React.MouseEvent,
    mentionId: string,
    mentionType: string
  ) => {
    // Stop propagation to prevent parent click handlers
    e.stopPropagation();
    e.preventDefault();

    // Call external onClick if provided (backward compatibility)
    if (onClick) {
      onClick(mentionId, mentionType);
    }

    // Handle built-in click actions
    if (mentionType === "employee" || mentionType === "employee-record") {
      // Extract actual ID (remove prefix)
      const actualId = mentionId.replace(/^(employee-record-|employee-)/, "");

      // Determine the type based on the prefix
      const type =
        mentionType === "employee-record" ? "employee-record" : "employee";

      dispatch(
        openEmployeeProfileSidebar({
          employeeId: actualId,
          type: type as "employee" | "employee-record",
        })
      );
    } else if (mentionType === "company-document") {
      // Find the document from Redux state
      const actualId = mentionId.replace(/^company-document-/, "");
      const document = companyDocuments?.find((doc) => doc.id === actualId);

      if (document) {
        dispatch(openDocumentPreviewModal({ document }));
      }
    }
  };

  const parseTextWithMentions = (inputText: string) => {
    const parts = parseMentions(inputText);

    return parts.map((part, index) => {
      if (part.type === "mention" && part.mentionData) {
        const { mentionData } = part;

        return (
          <span
            key={index}
            className={`mention-text mention-text--${mentionData.type}`}
            onClick={(e) =>
              handleMentionClick(e, mentionData.id, mentionData.type)
            }
            style={{
              cursor:
                mentionData.type === "employee" ||
                mentionData.type === "employee-record" ||
                mentionData.type === "company-document"
                  ? "pointer"
                  : "default",
            }}
            title={`${getMentionTypeLabel(mentionData.type)}: ${mentionData.display}`}>
            <span className="mention-text__icon">
              {mentionData.type === "employee" ? (
                <ValidatedImage
                  imageUrl={mentionData.avatarURL || ""}
                  fallbackUrl={
                    mentionData.fallbackAvatar ||
                    getDefaultUserAvatar(mentionData.display)
                  }
                  alt={mentionData.display}
                  className="mention-text__avatar"
                />
              ) : mentionData.type === "persona" ? (
                <img
                  src="/icons/light-mode/persona_manager_off.svg"
                  alt="Persona"
                  className="mention-text__avatar"
                />
              ) : mentionData.type === "user" ? (
                <ValidatedImage
                  imageUrl={mentionData.avatarURL || ""}
                  fallbackUrl={
                    mentionData.fallbackAvatar ||
                    getDefaultUserAvatar(mentionData.display)
                  }
                  alt={mentionData.display}
                  className="mention-text__avatar"
                />
              ) : mentionData.type === "employee-record" ? (
                <ValidatedImage
                  imageUrl={mentionData.avatarURL || ""}
                  fallbackUrl={
                    mentionData.fallbackAvatar ||
                    getDefaultUserAvatar(mentionData.display)
                  }
                  alt={mentionData.display}
                  className="mention-text__avatar"
                />
              ) : mentionData.type === "company-document" ? (
                <img
                  src="/icons/light-mode/note.svg"
                  alt="Company Document"
                  className="mention-text__avatar"
                  onError={(e) => {
                    // Fallback for company document icon if not found
                    e.currentTarget.style.display = "none";
                    const fallback = document.createElement("div");
                    fallback.textContent = "📋";
                    fallback.style.cssText =
                      "width: 20px; height: 20px; margin-right: 8px; display: flex; align-items: center; justify-content: center; font-size: 16px;";
                    e.currentTarget.parentNode?.insertBefore(
                      fallback,
                      e.currentTarget
                    );
                  }}
                />
              ) : mentionData.type === "resource" ? (
                <img
                  src="/icons/light-mode/collection_resources_off.svg"
                  alt="Resource"
                  className="mention-text__avatar"
                  onError={(e) => {
                    // Fallback for resource icon if not found
                    e.currentTarget.style.display = "none";
                    const fallback = document.createElement("div");
                    fallback.textContent = "📄";
                    fallback.style.cssText =
                      "width: 20px; height: 20px; margin-right: 8px; display: flex; align-items: center; justify-content: center; font-size: 16px;";
                    e.currentTarget.parentNode?.insertBefore(
                      fallback,
                      e.currentTarget
                    );
                  }}
                />
              ) : (
                <span className="mention-text__emoji">
                  {getMentionTypeIcon(mentionData.type)}
                </span>
              )}
            </span>
            <span className="mention-text__content">{part.content}</span>
          </span>
        );
      }

      // Regular text
      return (
        <span key={index} className="mention-text__regular">
          {part.content}
        </span>
      );
    });
  };

  const getMentionTypeIcon = (type: string): string => {
    switch (type) {
      case "employee":
        return ""; // No longer used for employees, we use avatars
      case "persona":
        return ""; // No longer used for personas, we use SVG
      case "user":
        return ""; // No longer used for users, we use avatars
      case "employee-record":
        return ""; // No longer used for employee records, we use avatars
      case "company-document":
        return ""; // No longer used for company documents, we use SVG
      case "resource":
        return "📄";
      default:
        return "•";
    }
  };

  const getMentionTypeLabel = (type: string): string => {
    switch (type) {
      case "employee":
        return "Employee";
      case "persona":
        return "Persona";
      case "user":
        return "User";
      case "employee-record":
        return "Employee Record";
      case "company-document":
        return "Company Document";
      case "resource":
        return "Resource";
      default:
        return "Mention";
    }
  };

  return (
    <span className={`mention-text-container ${className}`}>
      {parseTextWithMentions(text)}
    </span>
  );
};
